import { useApi } from '@/api';
import {
  Asset,
  Page,
  PageSkeleton,
  WalletHeading,
  RecentActivity
} from '@/components';
import { Flex, KpiCard } from '@blockanalitica/ui';
import { useParams } from 'react-router';
import HistoricGraphs from './HistoricGraphs';
import { useCallback } from 'react';
import { DEFAULT_NETWORK } from '@/constants';

type MarketWallet = {
  underlying_symbol: string;
  underlying_address: string;
  wallet_info: {
    total_supply: string | null;
    total_borrow: string | null;
    health_rate: string | null;
  };
};

export type ActivityEvent = {
  datetime: string;
  date: string;
  tx_hash: string;
  block_number: string;
  order_index: string;
  wallet_address: string;
  event: string;
  underlying_address: string;
  symbol: string;
  debt_symbol?: string;
  amount: string;
  amount_usd: string;
};

export default function MarketWalletPage() {
  const { marketAddress, walletAddress } = useParams() as {
    marketAddress: string;
    walletAddress: string;
  };
  const network = DEFAULT_NETWORK;

  const [data, error] = useApi<MarketWallet>({
    url: `/markets/${marketAddress}/wallets/${walletAddress}/`
  });

  const contentFn = useCallback(
    (item: ActivityEvent) => (
      <>
        {item.event} {item.symbol}
      </>
    ),
    []
  );

  if (error) {
    throw error;
  }

  if (!data) {
    return <PageSkeleton />;
  }

  return (
    <Page>
      <WalletHeading
        walletAddress={walletAddress}
        totalSupply={data.wallet_info.total_supply}
        totalBorrow={data.wallet_info.total_borrow}
        healthRate={data.wallet_info.health_rate}
        network={network}
      />

      <Asset
        symbol={data.underlying_symbol}
        address={data.underlying_address}
        network={network}
        iconSize="4"
        fontSize="3"
      />

      <Flex flexDirection={['column', 'row']} gap={['m', 's']}>
        <KpiCard
          title="Supplied"
          data={data}
          field="supply"
          options={{
            notation: 'compact'
          }}
        />

        <KpiCard
          title="Borrowed"
          data={data}
          field="borrow"
          options={{
            notation: 'compact'
          }}
        />
      </Flex>
      <HistoricGraphs
        marketAddress={data.underlying_address}
        underlyingSymbol={data.underlying_symbol}
        network={network}
        walletAddress={walletAddress}
      />

      <RecentActivity<ActivityEvent>
        activityAPIUrl={`/markets/${marketAddress}/wallets/${walletAddress}/events/`}
        activityContentFn={contentFn}
        heatmapAPIUrl={`/markets/${marketAddress}/wallets/${walletAddress}/heatmap/`}
        linkTo="activity"
      />
    </Page>
  );
}
